<template>
  <div class="establishment-search">
    <div class="establishment-search__body">
      <div class="establishment-search__mobile-tabs">
        <button
          type="button"
          class="establishment-search__tab"
          :class="{ 'establishment-search__tab--active': mobileView === 'list' }"
          @click="setMobileView('list')">
          {{ t('establishmentSearch.tabList') }}
        </button>
        <button
          type="button"
          class="establishment-search__tab"
          :class="{ 'establishment-search__tab--active': mobileView === 'map' }"
          @click="setMobileView('map')">
          {{ t('establishmentSearch.tabMap') }}
        </button>
      </div>

      <div class="establishment-search__split">
        <aside
          class="establishment-search__sidebar"
          :class="{
            'establishment-search__sidebar--hidden-mobile': mobileView === 'map',
          }">
          <div
            ref="sidebarInnerEl"
            class="establishment-search__sidebar-inner">
            <div class="establishment-search__sidebar-intro">
              <PrepeersBreadcrumb
                auto
                section="recherche"
                :container="false"
                inline
                class="establishment-search__breadcrumb" />

              <section
                class="hero-section hero-section--establishment-search establishment-search__sidebar-hero">
                <h1 class="hero-section__h1">{{ heroH1 }}</h1>
                <div
                  v-if="seoPayload?.introduction"
                  class="hero-section__intro"
                  v-html="formatSeoIntro(seoPayload.introduction)" />
                <div class="hero-section__line" />
              </section>
            </div>

            <div class="establishment-search__sidebar-top">
              <div class="establishment-search__toolbar">
                <PrePeersSelect
                  :model-value="props.displayKind"
                  :options="kindSelectOptions"
                  :label="t('establishmentSearch.kindLabel')"
                  @update:model-value="onKindChange" />
                <PrePeersSearchBar
                  v-model="searchDraft"
                  :placeholder="searchPlaceholder"
                  :submit-label="t('establishmentSearch.searchSubmit')"
                  @search="onSubmitSearch" />
              </div>

              <div class="establishment-search__filter-row">
                <EstablishmentMultiSelectFilter
                  :trigger-label="t('establishmentSearch.filterSchoolTypes')"
                  mode="flat"
                  :flat-options="ESTABLISHMENT_SCHOOL_TYPE_OPTIONS"
                  :model-value="typeSlugs"
                  @update:model-value="(slugs) => updateFilterQueryParam('type', slugs)" />
                <EstablishmentMultiSelectFilter
                  v-if="props.displayKind === 'program'"
                  :trigger-label="t('establishmentSearch.filterFormationTypes')"
                  mode="flat"
                  :flat-options="ESTABLISHMENT_FORMATION_TYPE_OPTIONS"
                  :model-value="formationTypeSlugs"
                  @update:model-value="(slugs) => updateFilterQueryParam('ftype', slugs)" />
                <EstablishmentMultiSelectFilter
                  :trigger-label="t('establishmentSearch.filterLevels')"
                  mode="grouped"
                  :sections="ESTABLISHMENT_LEVEL_SECTIONS"
                  :model-value="levelSlugs"
                  @update:model-value="(slugs) => updateFilterQueryParam('level', slugs)" />
                <EstablishmentMultiSelectFilter
                  :trigger-label="t('establishmentSearch.filterDomains')"
                  mode="flat"
                  :flat-options="ESTABLISHMENT_DOMAIN_OPTIONS"
                  :model-value="domainSlugs"
                  @update:model-value="(slugs) => updateFilterQueryParam('domain', slugs)" />
              </div>

              <div
                v-if="activeChips.length"
                class="establishment-search__active-bar">
                <div class="establishment-search__active-chips">
                  <PrePeersTag
                    v-for="chip in activeChips"
                    :key="`${chip.param}-${chip.slug}`"
                    size="sm"
                    removable
                    :label="chip.label"
                    :remove-aria-label="t('establishmentSearch.removeFilterChip')"
                    @remove="removeFilterChip(chip.param, chip.slug)" />
                </div>
                <button
                  type="button"
                  class="establishment-search__clear-all"
                  @click="onClearAllFilters">
                  {{ t('establishmentSearch.clearAll') }}
                </button>
              </div>

              <p
                v-if="!isSearchPending"
                class="establishment-search__count">
                {{ resultSummaryLine }}
              </p>
            </div>

            <div class="establishment-search__sidebar-scroll">
              <div class="establishment-search__cards">
                <template v-if="isSearchPending">
                  <PrePeersSkeleton
                    v-for="n in 6"
                    :key="`result-skeleton-${n}`"
                    variant="block"
                    height="128px"
                    border-radius="var(--radius-lg)" />
                </template>
                <template v-else>
                  <template v-if="props.displayKind === 'school'">
                    <div
                      v-for="(row, index) in schoolRows"
                      :key="row.id"
                      class="establishment-search__card-wrap"
                      :data-establishment-card-id="row.id"
                      :class="{
                        'establishment-search__card-wrap--hl': selectedMarkerId === row.id,
                      }"
                      @click="onSelectMarkerFromMap(row.id)"
                      @mouseenter="hoveredCardId = row.id"
                      @mouseleave="hoveredCardId = null">
                      <UiSchoolCard
                        :school="row.card"
                        @consult="trackEstablishmentResultConsulted(row, index)" />
                    </div>
                  </template>
                  <template v-else>
                    <div
                      v-for="(row, index) in programRows"
                      :key="row.id"
                      class="establishment-search__card-wrap"
                      :data-establishment-card-id="row.id"
                      :class="{
                        'establishment-search__card-wrap--hl': selectedMarkerId === row.id,
                      }"
                      @click="onSelectMarkerFromMap(row.id)"
                      @mouseenter="hoveredCardId = row.id"
                      @mouseleave="hoveredCardId = null">
                      <UiProgramCard
                        :program="row.program"
                        @consult="trackEstablishmentResultConsulted(row, index)" />
                    </div>
                  </template>
                  <p
                    v-if="resultCount === 0"
                    class="establishment-search__empty">
                    {{ t('establishmentSearch.noResults') }}
                  </p>
                </template>
              </div>
            </div>
          </div>

          <div
            v-if="!props.isLoggedIn"
            class="establishment-search__sidebar-footer establishment-search__sidebar-footer--fixed">
            <section
              class="cta-section establishment-search__cta establishment-search__cta--establishment-search">
              <div class="cta-card establishment-search__cta-bar">
                <div class="cta-card__avatar">
                  <NuxtImg
                    src="/images/lola-avatar.png"
                    alt="Lola"
                    class="cta-card__avatar-img"
                    width="52"
                    height="52"
                    loading="lazy"
                    decoding="async"
                    format="webp" />
                </div>
                <div class="establishment-search__cta-bar-text">
                  <h3 class="cta-card__title">{{ t('establishmentSearch.ctaTitle') }}</h3>
                  <p class="cta-card__desc">{{ t('establishmentSearch.ctaDesc') }}</p>
                </div>
                <NuxtLink
                  :to="localePath('/discuter')"
                  class="cta-card__btn establishment-search__cta-bar-btn"
                  @click="onEstablishmentSearchLolaCtaClick"
                  >{{ t('establishmentSearch.ctaBtn') }}</NuxtLink
                >
              </div>
            </section>
          </div>
        </aside>

        <div
          class="establishment-search__map-col"
          :class="{
            'establishment-search__map-col--hidden-mobile': mobileView === 'list',
          }">
          <div class="establishment-search__map-inner">
            <EstablishmentSearchMap
              :markers="mapMarkers"
              :selected-id="selectedMarkerId"
              :hovered-id="hoveredCardId"
              :format-more-count="formatMoreCount"
              :initial-bounds="initialMapBounds"
              :initial-zoom="6"
              :map-search-enabled="mapSearchEnabled"
              :map-search-label="t('establishmentSearch.mapSearchToggle')"
              :apply-bounds-label="t('establishmentSearch.mapApplyBounds')"
              :loading-label="t('establishmentSearch.mapLoading')"
              :loading="isSearchPending"
              :searching-label="t('establishmentSearch.mapSearching')"
              @update:map-search="onUpdateMapSearch"
              @apply-bounds="onApplyBounds"
              @bounds-change="onMapBoundsChange"
              @select-marker="onMapMarkerClicked" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, toRef } from 'vue'
import type { ComputedRef, PropType, Ref } from 'vue'

import { TRACKING_EVENTS } from '~/constants/trackingEvents'
import {
  toEstablishmentMapMarkerClickedPayload,
  toEstablishmentResultConsultedEntityFromProgramRow,
  toEstablishmentResultConsultedEntityFromSchoolRow,
  toEstablishmentResultConsultedPayload,
} from '~/composables/useTracking'
import PrePeersTag from '~/components/core/PrePeersTag.vue'
import PrePeersSelect from '~/components/core/PrePeersSelect.vue'
import PrePeersSearchBar from '~/components/form/PrePeersSearchBar.vue'
import PrePeersSkeleton from '~/components/ui/PrePeersSkeleton.vue'
import PrepeersBreadcrumb from '~/components/navigation/PrepeersBreadcrumb.vue'
import EstablishmentMultiSelectFilter from '~/components/search/EstablishmentMultiSelectFilter.vue'
import EstablishmentSearchMap from '~/components/search/EstablishmentSearchMap.vue'
import { useEstablishmentSearchHub } from '~/composables/useEstablishmentSearchHub'
import {
  ESTABLISHMENT_DOMAIN_OPTIONS,
  ESTABLISHMENT_FORMATION_TYPE_OPTIONS,
  ESTABLISHMENT_LEVEL_SECTIONS,
  ESTABLISHMENT_SCHOOL_TYPE_OPTIONS,
} from '~/composables/establishmentSearchFilterLists'
import type { EstablishmentSearchDisplayKind } from '~/types/EstablishmentSearch'
import type { EstablishmentSearchMapMarker } from '~/types/EstablishmentSearchMap'
import type {
  EstablishmentCoord,
  EstablishmentProgramRow,
  EstablishmentSchoolRow,
} from '~/types/EstablishmentSearchRows'
import type {
  EstablishmentResultConsultedEntity,
  EstablishmentSearchTrackingContext,
} from '~/types/Tracking'
import type { PrePeersSelectOption } from '~/types/PrePeersSelect'
import type { SearchEstablishmentsProps } from '~/types/SearchEstablishments'
import { formatSeoIntro } from '~/utils/helpers'

// ── Props ─────────────────────────────────────────────────

const props: SearchEstablishmentsProps = defineProps({
  displayKind: {
    type: String as PropType<EstablishmentSearchDisplayKind>,
    required: true,
  },
  isLoggedIn: {
    type: Boolean,
    default: false,
  },
})

// ── Composables ───────────────────────────────────────────

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const { track } = useTracking()

const {
  searchDraft,
  mobileView,
  typeSlugs,
  formationTypeSlugs,
  levelSlugs,
  domainSlugs,
  mapSearchEnabled,
  activeChips,
  setMobileView,
  onKindChange,
  updateFilterQueryParam,
  removeFilterChip,
  clearAllFilters,
  onUpdateMapSearch,
  onApplyBounds,
  onMapBoundsChange,
  onSubmitSearch,
  seoPayload,
  schoolRows,
  programRows,
  isSearchPending,
  initialMapBounds,
  lastEstablishmentSearchTrackingContext,
} = useEstablishmentSearchHub({
  displayKind: toRef(props, 'displayKind'),
})

// ── Refs (UI locale) ──────────────────────────────────────

const selectedMarkerId: Ref<string | null> = ref<string | null>(null)
const hoveredCardId: Ref<string | null> = ref<string | null>(null)
const sidebarInnerEl: Ref<HTMLElement | null> = ref<HTMLElement | null>(null)

// ── Computed (affichage) ──────────────────────────────────

/** Placeholder de couverture API : exclu du logo tooltip (réservé au fond de la carte école). */
const EMPTY_COVER_PLACEHOLDER = '/images/emptyCoverPicture.png'

const mapMarkers: ComputedRef<EstablishmentSearchMapMarker[]> = computed(
  (): EstablishmentSearchMapMarker[] => {
    if (props.displayKind === 'school') {
      // Une école peut avoir plusieurs campus → un marqueur par coordonnée (tous portent l'id de l'école).
      return schoolRows.value.flatMap(
        (row: EstablishmentSchoolRow): EstablishmentSearchMapMarker[] =>
          row.coords
            .filter(
              (coord: EstablishmentCoord): boolean =>
                Number.isFinite(coord.lat) && Number.isFinite(coord.lng),
            )
            .map(
              (coord: EstablishmentCoord): EstablishmentSearchMapMarker => ({
                id: row.id,
                name: row.card.Name,
                subtitle: row.card.Type || undefined,
                location: row.card.Locations.length ? row.card.Locations.join(', ') : undefined,
                logoUrl:
                  row.card.LogoImage && row.card.LogoImage !== EMPTY_COVER_PLACEHOLDER
                    ? row.card.LogoImage
                    : undefined,
                lat: coord.lat,
                lng: coord.lng,
              }),
            ),
      )
    }
    return programRows.value
      .filter(
        (row: EstablishmentProgramRow): boolean =>
          Number.isFinite(row.lat) && Number.isFinite(row.lng),
      )
      .map((row: EstablishmentProgramRow): EstablishmentSearchMapMarker => {
        const degreeAndStyle: string = [row.program.DegreeLevel.trim(), row.program.Style.trim()]
          .filter((part: string): boolean => part.length > 0)
          .join(' · ')
        return {
          id: row.id,
          name: row.program.Name,
          subtitle: row.program.SchoolName || undefined,
          detail: degreeAndStyle || undefined,
          location: row.program.LocationsString || undefined,
          logoUrl: row.program.LogoImage?.trim() || undefined,
          lat: row.lat,
          lng: row.lng,
        }
      })
  },
)
const kindSelectOptions: ComputedRef<PrePeersSelectOption[]> = computed(
  (): PrePeersSelectOption[] => [
    { value: 'school', label: t('establishmentSearch.kindSchool') },
    { value: 'program', label: t('establishmentSearch.kindProgram') },
  ],
)
const searchPlaceholder: ComputedRef<string> = computed((): string =>
  props.displayKind === 'school'
    ? t('establishmentSearch.searchPlaceholderSchool')
    : t('establishmentSearch.searchPlaceholderProgram'),
)
const heroH1: ComputedRef<string> = computed((): string => {
  const seoH1: string | undefined = seoPayload.value?.h1
  if (seoH1 && seoH1.trim().length > 0) return seoH1
  return props.displayKind === 'school'
    ? t('establishmentSearch.fallbackH1School')
    : t('establishmentSearch.fallbackH1Program')
})
const resultCount: ComputedRef<number> = computed((): number =>
  props.displayKind === 'school' ? schoolRows.value.length : programRows.value.length,
)
const resultSummaryLine: ComputedRef<string> = computed((): string => {
  const kindWord: string =
    props.displayKind === 'school'
      ? t('establishmentSearch.resultKindSchool')
      : t('establishmentSearch.resultKindProgram')
  return t('establishmentSearch.resultLine', { kind: kindWord, n: resultCount.value })
})

/**
 * Snapshot de la dernière recherche aboutie (`establishment_search_executed`).
 * Enrichit `establishment_result_consulted` avec query, filtres et géo au moment du clic.
 * Fallback si l'utilisateur clique « Consulter » avant le premier flush PostHog.
 */
const establishmentSearchContextForResultConsulted: ComputedRef<EstablishmentSearchTrackingContext> =
  computed((): EstablishmentSearchTrackingContext => {
    const snapshot: EstablishmentSearchTrackingContext | null =
      lastEstablishmentSearchTrackingContext.value
    if (snapshot) return snapshot

    return {
      display_kind: props.displayKind,
      query: '',
      filters: { type: [], domain: [], level: [], ftype: [] },
      results_count: resultCount.value,
      map_search_enabled: mapSearchEnabled.value,
    }
  })

// ── Fonctions (UI carte / liste) ───────────────────────────

/**
 * Formate le « et N de plus » du tooltip cluster (résolu ici pour garder le texte dans l'i18n).
 * @param {number} count - Nombre d'entités masquées au-delà du plafond.
 * @returns {string} Libellé traduit.
 */
function formatMoreCount(count: number): string {
  return t('establishmentSearch.mapTooltipMore', { count })
}

function onClearAllFilters(): void {
  clearAllFilters()
  selectedMarkerId.value = null
}

function scrollSidebarToCard(cardEl: HTMLElement): void {
  const scrollEl: HTMLElement | null = sidebarInnerEl.value
  if (scrollEl === null) {
    cardEl.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
    return
  }
  const scrollRect: DOMRect = scrollEl.getBoundingClientRect()
  const cardRect: DOMRect = cardEl.getBoundingClientRect()
  const marginTop: number = parseFloat(getComputedStyle(cardEl).scrollMarginTop)
  const scrollMarginTop: number = Number.isFinite(marginTop) ? marginTop : 0
  const nextTop: number = scrollEl.scrollTop + (cardRect.top - scrollRect.top) - scrollMarginTop
  scrollEl.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' })
}

/**
 * @returns {void}
 */
function onEstablishmentSearchLolaCtaClick(): void {
  track(TRACKING_EVENTS.CTA_LOLA_CLICKED, {
    variant: 'establishment_search_sidebar',
    entityName: 'recherche_etablissement',
    location: route.path,
  })
}

/**
 * @param {EstablishmentSchoolRow | EstablishmentProgramRow} row - Ligne résultat cliquée.
 * @param {number} listPosition - Index dans la liste (0-based).
 * @returns {void}
 */
function trackEstablishmentResultConsulted(
  row: EstablishmentSchoolRow | EstablishmentProgramRow,
  listPosition: number,
): void {
  const entity =
    props.displayKind === 'school'
      ? toEstablishmentResultConsultedEntityFromSchoolRow(row as EstablishmentSchoolRow)
      : toEstablishmentResultConsultedEntityFromProgramRow(row as EstablishmentProgramRow)

  track(
    TRACKING_EVENTS.ESTABLISHMENT_RESULT_CONSULTED,
    toEstablishmentResultConsultedPayload(
      establishmentSearchContextForResultConsulted.value,
      entity,
      listPosition,
    ),
  )
}

/**
 * Clic sur un marqueur de la carte (≠ clic sur une card) : track l'engagement carte
 * — quelle entité, dans quel contexte de recherche — puis délègue à la sélection/scroll.
 * @param {string} id - Id de l'entité du marqueur cliqué.
 * @returns {void}
 */
function onMapMarkerClicked(id: string): void {
  const row: EstablishmentSchoolRow | EstablishmentProgramRow | undefined =
    props.displayKind === 'school'
      ? schoolRows.value.find((r: EstablishmentSchoolRow): boolean => r.id === id)
      : programRows.value.find((r: EstablishmentProgramRow): boolean => r.id === id)
  if (row) {
    const entity: EstablishmentResultConsultedEntity =
      props.displayKind === 'school'
        ? toEstablishmentResultConsultedEntityFromSchoolRow(row as EstablishmentSchoolRow)
        : toEstablishmentResultConsultedEntityFromProgramRow(row as EstablishmentProgramRow)
    track(
      TRACKING_EVENTS.ESTABLISHMENT_MAP_MARKER_CLICKED,
      toEstablishmentMapMarkerClickedPayload(
        establishmentSearchContextForResultConsulted.value,
        entity,
      ),
    )
  }
  onSelectMarkerFromMap(id)
}

function onSelectMarkerFromMap(id: string): void {
  selectedMarkerId.value = id
  void nextTick((): void => {
    const safeId: string =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(id) : id
    const cardEl: Element | null = document.querySelector(
      `[data-establishment-card-id="${safeId}"]`,
    )
    if (cardEl instanceof HTMLElement) {
      scrollSidebarToCard(cardEl)
    }
  })
}

// ── SEO ───────────────────────────────────────────────────

useSeoMeta({
  title: (): string | undefined => seoPayload.value?.title,
  description: (): string | undefined => seoPayload.value?.description,
  ogTitle: heroH1,
  ogDescription: (): string | undefined => seoPayload.value?.description,
  ogType: 'website',
})
</script>

<style scoped>
.establishment-search {
  display: flex;
  flex-direction: column;
  height: calc(100dvh - var(--header-height));
  max-height: calc(100dvh - var(--header-height));
  min-height: 0;
  overflow: hidden;
}

/* Liste + carte : occupe tout l'espace sous la navbar, sans scroll de page. */
.establishment-search__body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.establishment-search__sidebar-intro {
  flex: 0 0 auto;
  position: relative;
  z-index: 3;
}

@media (min-width: 1024px) {
  .establishment-search__breadcrumb.breadcrumb--inline {
    padding: var(--space-4) var(--space-5) var(--space-2);
  }
}

.establishment-search__sidebar-hero.hero-section {
  padding: 0 var(--container-pad) var(--space-3);
  text-align: left;
}

.establishment-search__sidebar-hero .hero-section__h1 {
  margin-bottom: var(--space-2);
  font-size: var(--text-lg);
  line-height: var(--leading-snug);
}

.establishment-search__sidebar-hero .hero-section__intro {
  margin: 0 0 var(--space-3) 0;
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
}

.establishment-search__sidebar-hero .hero-section__intro :deep(p) {
  margin: 0 0 var(--space-2) 0;
}

.establishment-search__sidebar-hero .hero-section__intro :deep(p:last-child) {
  margin-bottom: 0;
}

.establishment-search__sidebar-hero .hero-section__line {
  margin-inline: 0;
  width: 56px;
  height: 3px;
}

@media (min-width: 1024px) {
  .establishment-search__sidebar-hero.hero-section {
    padding: 0 var(--space-5) var(--space-4);
  }

  .establishment-search__sidebar-hero .hero-section__h1 {
    font-size: var(--text-xl);
  }
}

.establishment-search__sidebar-footer {
  border-top: 1px solid var(--border-subtle);
  padding: var(--space-3) var(--container-pad);
  padding-bottom: calc(var(--space-3) + env(safe-area-inset-bottom, 0px));
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.06) 0%, rgba(0, 0, 0, 0.14) 100%);
  box-shadow: 0 -8px 28px rgba(0, 0, 0, 0.16);
}

/* CTA ancré en bas de la colonne aside (pas au viewport) : suit le scroll page, ne recouvre pas le footer site. */
.establishment-search__sidebar-footer--fixed {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  box-sizing: border-box;
}

@media (min-width: 1024px) {
  .establishment-search__sidebar-footer {
    padding: var(--space-3) var(--space-5);
    padding-bottom: calc(var(--space-3) + env(safe-area-inset-bottom, 0px));
  }
}

.establishment-search__sidebar-footer .cta-section {
  padding: 0;
}

.establishment-search__sidebar-footer .cta-card {
  margin: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-4);
  border-width: 3px;
  border-radius: var(--radius-lg);
  text-align: left;
}

.establishment-search__cta-bar-text {
  flex: 1 1 auto;
  min-width: 0;
}

.establishment-search__sidebar-footer .cta-card__avatar {
  width: auto;
  height: auto;
  margin: 0;
  flex-shrink: 0;
  overflow: visible;
}

/* Ne pas recadrer en cercle : la pastille « en ligne » fait partie du PNG (coin bas-droit). */
.establishment-search__sidebar-footer .cta-card__avatar img {
  display: block;
  width: 52px;
  height: auto;
}

.establishment-search__sidebar-footer .cta-card__title {
  font-size: var(--text-md);
  font-weight: 700;
  margin: 0 0 var(--space-2) 0;
  line-height: var(--leading-snug);
}

.establishment-search__sidebar-footer .cta-card__desc {
  font-size: var(--text-sm);
  margin: 0;
  line-height: var(--leading-relaxed);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

/* Même gabarit que la carte CTA compacte d'avant (pas la barre trop fine). */
.establishment-search__sidebar-footer .establishment-search__cta-bar-btn {
  flex-shrink: 0;
  padding: var(--space-3) var(--space-6);
  font-size: var(--text-sm);
  font-weight: 600;
  white-space: nowrap;
}

.establishment-search__mobile-tabs {
  display: flex;
  flex-shrink: 0;
  gap: 0;
  border-bottom: 1px solid var(--border-default);
  background: var(--bg-elevated);
}

@media (min-width: 1024px) {
  .establishment-search__mobile-tabs {
    display: none;
  }
}

.establishment-search__tab {
  flex: 1;
  padding: var(--space-4);
  border: none;
  background: transparent;
  font-weight: 600;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  cursor: pointer;
  border-bottom: 3px solid transparent;
}

.establishment-search__tab--active {
  color: var(--accent-blue);
  border-bottom-color: var(--accent-blue);
  background: var(--bg-surface);
}

.establishment-search__split {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

@media (min-width: 1024px) {
  .establishment-search__split {
    flex-direction: row;
    align-items: stretch;
  }
}

.establishment-search__map-col {
  flex: 1 1 auto;
  min-height: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
}

.establishment-search__map-inner {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
}

@media (min-width: 1024px) {
  .establishment-search__map-col {
    flex: 0 0 60%;
    max-width: 60%;
    min-width: 0;
    min-height: 0;
    padding: 0;
  }
}

.establishment-search__map-col--hidden-mobile {
  display: none;
  padding: 0;
}

@media (min-width: 1024px) {
  .establishment-search__map-col--hidden-mobile {
    display: block;
  }
}

.establishment-search__sidebar {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 4;
  border-top: none;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.06) 0%, rgba(0, 0, 0, 0.12) 100%);
}

/* Colonne gauche : scroll intro + filtres + résultats (CTA Lola en bandeau bas de colonne). */
.establishment-search__sidebar-inner {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

/* Réserve la place du bandeau CTA pour ne pas masquer le bas des cartes. */
.establishment-search__sidebar:has(.establishment-search__sidebar-footer--fixed)
  .establishment-search__sidebar-inner {
  padding-bottom: min(28vh, 10rem);
}

@media (min-width: 1024px) {
  .establishment-search__sidebar {
    flex: 0 0 40%;
    max-width: 40%;
    border-top: none;
    border-right: 1px solid var(--border-subtle);
  }
}

@media (min-width: 1280px) {
  .establishment-search__sidebar {
    flex: 0 0 45%;
    max-width: 45%;
  }

  .establishment-search__map-col {
    flex: 0 0 55%;
    max-width: 55%;
  }
}

.establishment-search__sidebar--hidden-mobile {
  display: none;
}

@media (min-width: 1024px) {
  .establishment-search__sidebar--hidden-mobile {
    display: block;
  }
}

/* Bloc filtres + recherche : suit le scroll de la colonne (panneaux filtres en position absolute). */
.establishment-search__sidebar-top {
  flex: 0 0 auto;
  position: relative;
  z-index: 2;
  overflow: visible;
  padding: var(--space-4) var(--container-pad);
}

@media (min-width: 1024px) {
  .establishment-search__sidebar-top {
    padding: var(--space-6) var(--space-5) var(--space-4);
  }
}

.establishment-search__sidebar-scroll {
  flex: 0 0 auto;
  min-height: 0;
  overflow: visible;
  padding: var(--space-2) var(--container-pad) var(--space-4);
}

@media (min-width: 1024px) {
  .establishment-search__sidebar-scroll {
    padding: var(--space-2) var(--space-5) var(--space-4);
  }
}

.establishment-search__toolbar {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  margin-bottom: var(--space-3);
}

.establishment-search__toolbar :deep(.prepeers-select) {
  /* Le composant utilise `flex: 1 1 200px` (ligne de filtres) : en colonne ça étire le bloc et écarte la searchbar. */
  flex: 0 0 auto;
  align-self: stretch;
  min-width: 0;
  gap: var(--space-1);
}

.establishment-search__toolbar :deep(.prepeers-search-bar) {
  flex: 0 0 auto;
  align-self: stretch;
  width: 100%;
  max-width: none;
}

@media (max-width: 1023px) {
  .establishment-search__toolbar :deep(.prepeers-search-bar) {
    flex-direction: column;
    align-items: stretch;
  }

  .establishment-search__toolbar :deep(.prepeers-search-bar__field-wrap) {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
  }

  .establishment-search__toolbar :deep(.prepeers-search-bar__btn) {
    width: 100%;
    align-self: stretch;
    justify-content: center;
  }
}

@media (min-width: 1024px) {
  .establishment-search__toolbar {
    gap: var(--space-6);
    margin-bottom: var(--space-4);
  }
}

/* Carte : occupe toute la hauteur du conteneur flex (mobile + desktop). */
.establishment-search__map-inner :deep(.establishment-search-map),
.establishment-search__map-inner :deep(.establishment-search-map--fallback) {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}

/* Mobile : sans bordure sur la carte pour coller au split (desktop garde la bordure du composant carte). */
@media (max-width: 1023px) {
  .establishment-search__map-inner :deep(.establishment-search-map),
  .establishment-search__map-inner :deep(.establishment-search-map--fallback) {
    border: none;
  }
}

.establishment-search__filter-row {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.establishment-search__filter-row > * {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.establishment-search__filter-row :deep(.establishment-multi-filter) {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

/* Déclencheurs filtres : même largeur de cellule, texte tronqué, pas de scroll horizontal. */
.establishment-search__filter-row :deep(.establishment-multi-filter__trigger) {
  display: flex;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  align-items: center;
  box-sizing: border-box;
}

.establishment-search__filter-row :deep(.establishment-multi-filter__trigger-label) {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
}

.establishment-search__filter-row :deep(.establishment-multi-filter__chev) {
  margin-left: auto;
  flex-shrink: 0;
}

@media (min-width: 1024px) {
  .establishment-search__filter-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--space-3);
    margin-bottom: var(--space-6);
    align-items: stretch;
  }

  /* 4ᵉ filtre (formations) : une ligne pleine largeur sous la grille 3 colonnes. */
  .establishment-search__filter-row > *:nth-child(4) {
    grid-column: 1 / -1;
  }
}

.establishment-search__active-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: var(--space-3);
  margin-bottom: var(--space-3);
}

@media (min-width: 1024px) {
  .establishment-search__active-bar {
    margin-bottom: var(--space-4);
  }
}

.establishment-search__active-chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  flex: 1 1 auto;
}

.establishment-search__clear-all {
  flex: 0 0 auto;
  border: none;
  background: none;
  color: var(--accent-orange);
  font-weight: 600;
  font-size: var(--text-sm);
  cursor: pointer;
  text-decoration: underline;
}

.establishment-search__count {
  margin: var(--space-3) 0 var(--space-2);
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--text-primary);
}

@media (min-width: 1024px) {
  .establishment-search__count {
    margin-top: var(--space-4);
  }
}

.establishment-search__cards {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.establishment-search__card-wrap {
  cursor: pointer;
  /* Laisse un peu d'air sous le bord haut du scroll quand on centre depuis la carte (`scrollIntoView` block: start). */
  scroll-margin-top: var(--space-3);
}

.establishment-search__card-wrap--hl {
  outline: 2px solid var(--accent-orange);
  outline-offset: 2px;
  border-radius: var(--radius-lg);
}

.establishment-search__card-wrap :deep(.school-card),
.establishment-search__card-wrap :deep(.program-card) {
  transition: 0.1s ease-out;
}

.establishment-search__card-wrap:hover :deep(.school-card),
.establishment-search__card-wrap:hover :deep(.program-card) {
  transform: scale(1.01);
  background:
    linear-gradient(rgba(0, 0, 0, 0.14), rgba(0, 0, 0, 0.14)),
    linear-gradient(135deg, var(--bg-surface) 0%, var(--bg-elevated) 100%);
}

.establishment-search__empty {
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  margin: var(--space-6) 0;
}

/* CTA Lola dans le panneau latéral : version compacte (plus de marge page pleine). */
.establishment-search__cta {
  padding-top: 0;
}

.establishment-search__cta.establishment-search__cta--establishment-search {
  margin-top: 0;
}

@media (min-width: 1024px) {
  .establishment-search__cta.establishment-search__cta--establishment-search {
    margin-top: 0;
  }
}
</style>
